/**
 * © 2024 Little Shilling, Inc.
 * Shon Little
 * Created: 2024-02-07
 */

// Add third-party dependencies.
import { Link as RouterLink } from 'react-router-dom';
import Image from 'mui-image';
import { useTheme, useMediaQuery, Box, Paper, Typography, Link as MuiLink } from '@mui/material';

// Add local dependences.
import releveStudiosBg from '../../assets/images/releve-studios-bg.png';
import studioOne from '../../assets/images/studio-one.png';
import SpecialOfferWidget from '../Common/SpecialOfferWidget';

/**
 * Contact component.
 * @example
 * return (
 *   <Contact />
 * )
 * @returns {React.ReactElement} component.
 */
const Contact = () => {
  // Get theme.
  const theme = useTheme();

  // Use the useMediaQuery hook with the theme's breakpoints.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Set component styles.
  const styles = {
    root: {
      [theme.breakpoints.up('md')]: {
        bgcolor: theme.palette.primary.main,
        backgroundImage: `url(${releveStudiosBg})`,
        width: '100%',
        height: 'calc(100vh - 95px - 101.3px)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'top center',
        backgroundAttachment: 'fixed',
        top: 0,
        left: 0,
        zIndex: -1,
        paddingTop: '16px',
        overflowX: 'hidden',
      },
    },
    container: {
      width: '100%',
      textAlign: 'center',
    },
    paper: {
      maxWidth: 557,
      margin: 'auto',
      padding: theme.spacing(2),
    },
    photoPaper: {
      position: 'relative',
    },
    photoCredit: {
      position: 'absolute',
      left: 2,
      color: theme.palette.common.white,
      fontSize: '0.5rem',
      [theme.breakpoints.down('md')]: {
        bottom: 0,
      },
      [theme.breakpoints.up('md')]: {
        bottom: 100,
      },
    },
    glow: {
      textShadow: '-1px -1px 2px #ffffff, 0 0 25px #ffffff, 0 0 5px #ffffff',
    },
  };

  // Render component.
  return (
    <Box sx={styles.root}>
      <Box sx={styles.container}>
        <Typography variant="h1" gutterBottom sx={styles.glow}>
          Contact Us
        </Typography>
        <Box my={1}>
          <Paper sx={styles.paper}>
            <Typography my={2}>
              If you did not find your answers using our website, you can contact us directly through phone, in person
              or by email using the form below.
            </Typography>
            <MuiLink href="tel:+1-818-654-4140" color="inherit">
              <Typography my={2}>Phone: 818-654-4140</Typography>
            </MuiLink>
            <Box my={1}>
              <MuiLink component={RouterLink} to="/location" color="inherit">
                <Typography my={2}>Address: 8766 Corbin Ave, Northridge, CA 91324</Typography>
              </MuiLink>
            </Box>
          </Paper>
        </Box>
        <SpecialOfferWidget />
      </Box>
      {isMobile && (
        <Box my={3}>
          <Paper elevation={3} sx={styles.photoPaper}>
            <Image src={studioOne} alt="Studio One" />
            <Typography variant="body2" sx={styles.photoCredit}>
              Photography by Amy Bartlam
              <br />
              Interior Architectural Design and Construction by Four Point Design Build Inc
            </Typography>
          </Paper>
        </Box>
      )}
      {!isMobile && (
        <Box>
          <Typography variant="body2" sx={styles.photoCredit}>
            Photography by Amy Bartlam
            <br />
            Interior Architectural Design and Construction by Four Point Design Build Inc
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Export component.
export default Contact;
